import OpenAI from 'openai';
import { z } from 'zod';

if (!import.meta.env.VITE_OPENAI_API_KEY) {
  throw new Error('OpenAI API key is not configured. Please add VITE_OPENAI_API_KEY to your .env file.');
}

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

// Define schema for follow-up tasks
const FollowUpSchema = z.object({
  title: z.string().min(1),
  description: z.string(),
  assignee: z.string().nullable(),
  dueDate: z.string().nullable(),
  priority: z.enum(['high', 'medium', 'low'])
});

// Define schema for the complete summary response
const SummaryResponseSchema = z.object({
  summary: z.string().min(1),
  keyPoints: z.array(z.string()),
  followUps: z.array(FollowUpSchema)
});

type SummaryResponse = z.infer<typeof SummaryResponseSchema>;

// JSON Schema for Responses API structured output
const summaryJsonSchema = {
  type: "object" as const,
  properties: {
    summary: { type: "string" },
    keyPoints: {
      type: "array",
      items: { type: "string" }
    },
    followUps: {
      type: "array",
      items: {
        type: "object",
        properties: {
          title: { type: "string" },
          description: { type: "string" },
          assignee: { type: ["string", "null"] },
          dueDate: { type: ["string", "null"] },
          priority: {
            type: "string",
            enum: ["high", "medium", "low"]
          }
        },
        required: ["title", "description", "assignee", "dueDate", "priority"],
        additionalProperties: false
      }
    }
  },
  required: ["summary", "keyPoints", "followUps"],
  additionalProperties: false
};

// Error class for OpenAI-specific errors
class OpenAIError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'OpenAIError';
  }
}

export const generateSummary = async (transcript: string) => {
  try {
    if (!transcript.trim()) {
      throw new OpenAIError('Transcript cannot be empty');
    }

    // Using Responses API with GPT-5 and structured output
    const completion = await openai.beta.chat.completions.create({
      model: "gpt-5",
      temperature: 0.7,
      max_tokens: 4000,
      messages: [
        {
          role: "system",
          content: `You are a highly efficient meeting summarizer and task extractor. Analyze meeting transcripts to:
1. Create concise summaries that capture the main points and decisions
2. Extract key discussion points and important takeaways
3. Identify specific, actionable follow-up tasks
4. Assign clear owners for tasks when mentioned in the transcript
5. Set reasonable deadlines based on context and urgency
6. Prioritize tasks as high, medium, or low based on discussion context

Format the response as JSON with:
- summary: A concise meeting summary
- keyPoints: Array of key discussion points
- followUps: Array of tasks with:
  - title: Clear, actionable task title
  - description: Detailed task description
  - assignee: Task owner (if mentioned, otherwise null)
  - dueDate: Suggested due date (if mentioned, otherwise null)
  - priority: "high", "medium", or "low" based on urgency and impact`
        },
        {
          role: "user",
          content: `Please analyze this meeting transcript and extract all relevant information:\n\n${transcript}`
        }
      ],
      // New Responses API structured output format
      response_format: {
        type: "json_schema",
        json_schema: {
          name: "meeting_summary",
          strict: true,
          schema: summaryJsonSchema
        }
      }
    });

    const content = completion.choices[0].message.content;
    if (!content) {
      throw new OpenAIError('No content received from OpenAI');
    }

    let parsedContent: unknown;
    try {
      parsedContent = JSON.parse(content);
    } catch (error) {
      throw new OpenAIError('Failed to parse OpenAI response as JSON', error);
    }

    // Validate response against Zod schema
    const validationResult = SummaryResponseSchema.safeParse(parsedContent);
    if (!validationResult.success) {
      throw new OpenAIError('Invalid response format from OpenAI', validationResult.error);
    }

    const response = validationResult.data;

    // Set default due dates for tasks without specific deadlines
    const enhancedFollowUps = response.followUps.map((task: { dueDate: string | null; assignee: string | null }) => ({
      ...task,
      dueDate: task.dueDate || new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
      assignee: task.assignee || null
    }));

    return { ...response, followUps: enhancedFollowUps };
  } catch (error) {
    const message = error instanceof OpenAIError ? error.message : 'Failed to generate summary';
    throw new OpenAIError(message, error);
  }
};